import React from "react";
import { Tag } from "antd";
import { formatCurrencyWithConversion } from "../../../../utils/currencyUtils";

/**
 * Generate transaction columns for the payment allocation table
 * @param {Object} allocationStatus - The current allocation status information
 * @returns {Array} Array of column definitions for transaction table
 */
const getTransactionColumns = (allocationStatus) => [
  {
    title: "Transaction ID #",
    dataIndex: "transaction_id",
    key: "transaction_id",
    render: (value, record) => value || record.id || "-",
  },
  { title: "Detail", dataIndex: "detail_display", key: "detail" },
  { title: "Bank", dataIndex: "bank_name", key: "bank_name" },
  {
    title: "Posting Date",
    dataIndex: "transaction_date",
    key: "transaction_date",
    render: (date) => (date ? new Date(date).toLocaleDateString() : ""),
  },
  { title: "Reference", dataIndex: "reference", key: "reference" },
  { title: "Description", dataIndex: "description", key: "description" },
  { title: "Type", dataIndex: "type_display", key: "type" },
  {
    title: "Amount",
    dataIndex: "amount",
    key: "amount",
    render: (amount, record) => {
      const currency = record?.currency || record?.originalData?.currency || "$";
      return formatCurrencyWithConversion(amount || 0, currency);
    },
  },
  {
    title: "Allocated",
    key: "allocated",
    render: (_, record) => {
      if (allocationStatus) {
        const currency = record?.currency || record?.originalData?.currency || "$";
        const allocatedAmount = allocationStatus.totalAllocated || 0;
        return (
          <span>
            {formatCurrencyWithConversion(allocatedAmount, currency)} (
            {allocationStatus.percentAllocated.toFixed(2)}%)
          </span>
        );
      }
      return "0.00 (0%)";
    },
  },
  {
    title: 'Remaining Amount',
    key: 'unallocated',
    render: (record) => {
      const currency = record?.currency || record?.originalData?.currency || "$";
      
      // CRITICAL: Use the actual remaining_amount from the transaction record
      // This includes BOTH allocations AND overpayments
      // If remaining_amount is available from the backend, use it directly
      if (record.remaining_amount !== undefined && record.remaining_amount !== null) {
        return formatCurrencyWithConversion(Math.abs(parseFloat(record.remaining_amount)), currency);
      }
      
      // Fallback: Calculate manually (includes overpayments)
      if (record.amount) {
        const totalAmount = Math.abs(parseFloat(record.amount) || 0);
        const allocatedAmount = parseFloat(record.allocated_amount || allocationStatus?.totalAllocated || 0);
        const overpaymentAmount = parseFloat(record.overpayment_amount || 0);
        const remainingAmount = totalAmount - allocatedAmount - overpaymentAmount;
        
        // Always show as positive remaining amount (how much more can be allocated)
        return formatCurrencyWithConversion(Math.max(0, remainingAmount), currency);
      }
      return formatCurrencyWithConversion(0, currency);
    }
  },
  {
    title: "Allocation Status",
    key: "allocation_status",
    render: (_, record) => {
      // Check for overpayments first - takes precedence
      const overpaymentAmount = parseFloat(record?.overpayment_amount || 0);
      if (overpaymentAmount > 0) {
        return <Tag color="blue">Overpay</Tag>;
      }
      
      if (allocationStatus) {
        return (
          <Tag color={allocationStatus.color}>{allocationStatus.label}</Tag>
        );
      }
      return <Tag color="default">Unallocated</Tag>;
    },
  },
];

export default getTransactionColumns;
